import { useAuthStore } from '@/shared/auth/store';
import { handleUnauthorized } from '@/shared/auth/on401';
import { currentWorkstationId } from '@/shared/workstation/store';
import { ApiError, type ProblemDetail } from './types';
import { noteDataWrite } from './dataWrites';
import { isTimeoutError, READ_TIMEOUT_MESSAGE } from './timeout';

/**
 * Device-scoped workstation identity (ADR-0039 part A). Sent when the operator has picked a
 * station under the upper-right user menu; omitted entirely otherwise, so the BE sees no
 * difference from today. **No backend code reads this header yet** — it is the plumbing the
 * `crt_configuration` / `workstation_setting` follow-up needs. A header rather than a JWT claim
 * because the JWT is minted once at login and is the tenant boundary (ADR-0026), while a station
 * can change mid-session without re-auth.
 */
export const WORKSTATION_HEADER = 'X-PH10-Workstation';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  json?: unknown;
  /** Multipart body (file upload). When set, the browser sets the multipart Content-Type +
   *  boundary itself — do NOT also pass `json`. Token/401 handling is unchanged. */
  form?: FormData;
  /** When true, a 401 from the BE will NOT clear the local session. Use for endpoints where
   *  401 means "this specific re-auth failed" rather than "JWT expired" -- e.g. the
   *  change-password endpoint returns 401 when the supplied currentPassword is wrong, and we
   *  don't want a typo there to bounce the operator out of the session. Default false (a 401
   *  with a stored token means our token went stale and the route guard should redirect). */
  keepSessionOn401?: boolean;
}

/**
 * Single sanctioned HTTP entry point (anti-drift §1).
 *
 * - Sends/receives JSON; sets Accept and Content-Type appropriately.
 * - Attaches the bearer token from the auth store when present. Login/auth endpoints can pass
 *   an empty token by virtue of the auth store being unset; the backend's permitAll covers them.
 * - Translates non-2xx responses into ApiError carrying a parsed ProblemDetail.
 * - 401 goes through {@link handleUnauthorized}: the local session is cleared with a reason
 *   (shown on the login page; the route guard preserves where the user was), except for a
 *   kiosk display, which is asked to renew rather than being bounced to /login.
 */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { json, form, headers, keepSessionOn401, ...rest } = opts;
  const token = useAuthStore.getState().session?.token;
  const workstationId = currentWorkstationId();
  const init: RequestInit = {
    credentials: 'same-origin',
    ...rest,
    headers: {
      Accept: 'application/json',
      // Multipart: let the browser set Content-Type (with boundary). JSON: set explicitly.
      ...(json !== undefined && form === undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(workstationId ? { [WORKSTATION_HEADER]: workstationId } : {}),
      ...headers,
    },
    ...(form !== undefined
      ? { body: form }
      : json !== undefined
        ? { body: JSON.stringify(json) }
        : {}),
  };

  const res = await fetch(path, init);

  if (res.status === 401 && token && !keepSessionOn401) {
    // Stored session is no longer valid (expired or revoked). slice-3560 (ADR-0046): the rule
    // moved into handleUnauthorized so all six fetch paths share ONE policy — clear WITH a
    // reason the login page shows (RequireAuth already preserves the route), and never clear
    // a kiosk display on a transient 401; ask it to renew instead.
    handleUnauthorized();
  }

  // slice-3952: a successful write tells the cached lists to re-read on their next showing
  // (PB re-retrieves the list on return from a New / maintenance sheet).
  if (res.ok) noteDataWrite(init.method, path);

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json') || contentType.includes('+json');

  if (!res.ok) {
    const problem = isJson
      ? ((await res.json().catch(() => null)) as ProblemDetail | null)
      : null;
    const err = new ApiError(
      problem ?? {
        type: 'about:blank',
        title: res.statusText || 'Request failed',
        status: res.status,
      },
    );
    // ADR-0077: a READ that ran out of time says what to do about it, on every screen (the
    // Item Search slice-4394 wording, app-wide). Writes keep the server's own text.
    const method = (init.method ?? 'GET').toUpperCase();
    if ((method === 'GET' || method === 'HEAD') && res.status !== 401 && res.status !== 403 && isTimeoutError(err)) {
      throw new ApiError({ ...err.problem, detail: READ_TIMEOUT_MESSAGE });
    }
    throw err;
  }

  if (!isJson) {
    return undefined as T;
  }
  return (await res.json()) as T;
}
